import { Progress, Skeleton } from 'antd';
import React, { Fragment, useMemo } from 'react';
import useResponsiveCheck from '../../hooks/useResponsiveCheck';
import AudioSupport4 from './AudioSupport4';
import BatteryAndConnection from './BatteryAndConnection';
import { EditUnitName } from './EditUnitName';
import NormalFlowGoLiveButton from './NormalFlowGoLiveButton';
import SelectSWVersion from './SelectSWVersion';
import StatusAndAPNSetting from './StatusAndAPNSetting';
import StreamToolsGoLiveButton from './StreamToolsGoLiveButton';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

export const UnitHeaderView = ({
  t,
  unit,
  isEdit,
  renameUnit,
  cancelRenameUnit,
  changedelayMode,
  stopStream,
  showLoader,
  unitsettingsHandler,
  showLowDelay,
  removeUnit,
  handleStartStream,
  goLiveDisabled,
  goLive,
  streamToolsUnitsList,
  goLiveDisableForStreamtools,
  stopUnit,
  streamToolData,
  handleSRTCondition,
  swVersionList,
  onSaveUpdateSwVersion,
  sw_version,
  selectedSWversionId,
  onClickWithoutChangeSWVersion,
  isSwVersionSelectFieldTouch,
  onTouchSwVersionSelectField,
  onHandleSwVersionWarningMessage,
  onUpdateAudioSupport4Channel,
  audioSupportChannelVal,
  unitServices,
  isStreamingStarted,
}) => {
  const { isMobile, isBelow768 } = useResponsiveCheck();
  const language = cookies.get('language');
  const isLanguageRuOrFr = useMemo(
    () => ['fr', 'ru'].includes(language),
    [language],
  );

  const isSoloPROAndSRTService =
    unit?.product === 'LU_SOLO_PRO' &&
    unitServices?.includes('srt-cloud-caller');
  const isUnitOnline = unit.status !== 'offline';
  const isAudioSupport4disabled =
    ['streaming', 'offline'].includes(unit.status) || isStreamingStarted;

  const renderLowDelay = () => {
    if (!showLowDelay || !isUnitOnline) return null;
    const { product, delay, status } = unit;

    const disabled = status === 'streaming' || isStreamingStarted;
    if (product === 'LU_SOLO_PRO') {
      const delayOptions = [800, 1000, 5000];
      return (
        <div className='lowdelaymode'>
          <div className='lbltxt lrttxt'>{t('LOWDELAY')}:</div>
          <div className='three-toggle-wrapper'>
            {delayOptions.map((value) => (
              <label
                key={value}
                className={`delay-option ${delay === value ? 'selected' : ''} ${
                  disabled ? 'disabled' : ''
                }`}
              >
                <input
                  type='radio'
                  name='delayToggle'
                  value={value}
                  checked={delay === value}
                  onChange={() => changedelayMode(unit, value)}
                  disabled={disabled}
                />
                {value === 800 ? '800ms' : value === 1000 ? '1s' : '5s'}
              </label>
            ))}
          </div>
        </div>
      );
    }

    const toggleState = delay === 1000 ? 5000 : 1000;
    return (
      <div className='lowdelaymode d-flex align-items-center'>
        <div className='lbltxt lrttxt'>{t('LOWDELAY')}:</div>
        <div className='lowdelay-valuetxt ml-2'>
          <input
            type='checkbox'
            className='toggle'
            id='lowDelaytoggle'
            checked={delay !== 5000}
            onChange={() => changedelayMode(unit, toggleState)}
            disabled={disabled}
          />
          <label
            htmlFor='lowDelaytoggle'
            className='lowdelay-toogle mb-0'
            disabled={disabled} // For consistent CSS targeting
          >
            <span className='on'>On</span>
            <span className='off'>Off</span>
          </label>
        </div>
      </div>
    );
  };

  const renderAudioSupport = () =>
    isUnitOnline &&
    isSoloPROAndSRTService && (
      <AudioSupport4
        isSoloPROAndSRTService={isSoloPROAndSRTService}
        unitBossId={unit.BOSSID}
        audioSupportChannelVal={audioSupportChannelVal}
        isConfigMode={isEdit}
        disabled={isAudioSupport4disabled}
        onUpdateAudioSupport4Channel={onUpdateAudioSupport4Channel}
      />
    );

  const goLiveButton = () => {
    const streamToolInfo = streamToolsUnitsList?.[unit.BOSSID];
    if (streamToolInfo?.isLoadingData) {
      return (
        <Skeleton
          className='btn-skeleton'
          avatar={{ shape: 'square', size: 'large', style: { color: '#fff' } }}
          title={false}
          paragraph={false}
          active
        />
      );
    }
    if (streamToolInfo?.isStreamToolActive) {
      return (
        <StreamToolsGoLiveButton
          {...{
            streamToolsUnitsList,
            unit,
            goLiveDisableForStreamtools,
            stopUnit,
            goLive,
            t,
            handleSRTCondition,
            isLanguageRuOrFr,
          }}
        />
      );
    }
    return (
      <NormalFlowGoLiveButton
        {...{
          showLoader,
          unit,
          t,
          stopStream,
          goLiveDisabled,
          handleStartStream,
          isLanguageRuOrFr,
        }}
      />
    );
  };

  return (
    <div className='header-view-main'>
      <div className='titlebar'>
        {/* Left */}
        <div className='titlebarLeft'>
          <div className='unitname'>
            {isEdit ? (
              <EditUnitName
                {...{
                  unit,
                  renameUnit,
                  cancelRenameUnit,
                  removeUnit,
                  sw_version,
                  selectedSWversionId,
                  onSaveUpdateSwVersion,
                  onClickWithoutChangeSWVersion,
                  isSwVersionSelectFieldTouch,
                  onTouchSwVersionSelectField,
                  onHandleSwVersionWarningMessage,
                }}
              />
            ) : (
              <span className='editUnitnName' title={unit.name}>
                {unit.name}
              </span>
            )}
            {!isMobile && <BatteryAndConnection unit={unit} />}
          </div>

          {isEdit ? (
            <SelectSWVersion
              unit={unit}
              unitSn={unit.SN}
              swOptions={swVersionList}
              renameUnit={renameUnit}
              cancelRenameUnit={cancelRenameUnit}
              removeUnit={removeUnit}
            />
          ) : (
            <div className='smalltxt mb-1'>
              {unit.SN} - {unit.sw_version}
            </div>
          )}
        </div>

        {/* Right */}
        <div className='titlebarRight'>
          <div className='top-section'>
            {!isMobile && (!showLowDelay || (showLowDelay && !isEdit)) && (
              <StatusAndAPNSetting
                t={t}
                unit={unit}
                unitsettingsHandler={unitsettingsHandler}
              />
            )}
            {isEdit && !isBelow768 && renderLowDelay()}

            <div className='golive-toggle-controls'>
              {isUnitOnline && isBelow768 && (
                <Fragment>
                  {renderAudioSupport()}
                  {renderLowDelay()}
                </Fragment>
              )}
              <div className='statusdiv'>
                {goLiveButton()}
                {streamToolData?.isStreamToolActive &&
                  streamToolData?.progress > 0 && (
                    <div className='mt-1'>
                      <Progress
                        percent={
                          streamToolData.progress < 7
                            ? streamToolData.logProgress
                            : 100
                        }
                        showInfo
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>

          <div
            className={`bottom-section ${
              isMobile && !isEdit
                ? 'd-flex align-items-center'
                : !isBelow768 && !isEdit
                ? 'bottom-section-isEdit'
                : ''
            }`}
          >
            {isUnitOnline && !isBelow768 && !isEdit && renderLowDelay()}
            {!isBelow768 && renderAudioSupport()}
            {isMobile && (
              <div className='bottom-controls ml-2'>
                <StatusAndAPNSetting
                  t={t}
                  unit={unit}
                  unitsettingsHandler={unitsettingsHandler}
                />
                <BatteryAndConnection unit={unit} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
